// Real past charts for the games: a few random coins and windows per
// timeframe per session (each fetched once, then cut at many random
// moments), the 4h candles beside them, and a window of the engine's 1h
// signals for each signals game.
import type { Candle } from "../chart/indicators";
import type { ChartSignal, ChartSignalsResponse } from "../chart/chartSignals";
import { questionProblems } from "./grade";
import { makeRng } from "./rng";
import { TF_SEC, type GameData, type GameDef, type GameTf, type Question, type Rng } from "./types";

/** Liquid coins with years of history: a random one per window. */
export const GAME_COINS = [
  "BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT", "DOGEUSDT", "ADAUSDT",
  "AVAXUSDT", "LINKUSDT", "LTCUSDT", "DOTUSDT", "NEARUSDT", "ATOMUSDT",
] as const;

const DAY = 86_400;
/** Windows end somewhere in the year before now. */
const LOOKBACK_SEC = 365 * DAY;
/** Windows (each a random coin and time) per timeframe in a session, so its questions don't all show one coin. */
const WINDOWS_PER_TF = 3;
/** Candles per window (the backend serves at most 1500 a request). */
const WINDOW_BARS: Record<GameTf, number> = { "15m": 1200, "1h": 1200, "4h": 1000, "1d": 700 };
/** The 4h candles kept before a window, for the 4h bias (it reads 259). */
const HTF_BEFORE = 300;
/** The engine's signals: one 30-day 1h window, with this much history before it and room after. */
const SIGNAL_DAYS = 30;
const SIGNAL_HISTORY_BARS = 260;
const SIGNAL_AFTER_BARS = 48;
/** A cold past window is a fresh backtest on the server: 10–20 s. */
const SIGNALS_TIMEOUT_MS = 60_000;

export const coinOf = (symbol: string) => symbol.replace(/USDT$/, "");

/** Bar open time on the timeframe's grid. */
const align = (sec: number, tfSec: number) => Math.floor(sec / tfSec) * tfSec;

/**
 * A window ending at a random time in the year before now (minus `keepSec`,
 * so it has room after it).
 */
export function randomWindowEnd(nowSec: number, tfSec: number, keepSec: number, rng: Rng): number {
  const latest = nowSec - keepSec;
  return align(latest - rng.next() * LOOKBACK_SEC, tfSec);
}

async function fetchCandles(symbol: string, tf: string, fromSec: number, toSec: number, signal?: AbortSignal): Promise<Candle[]> {
  const res = await fetch(`/api/candles/${symbol}/${tf}?from=${Math.floor(fromSec)}&to=${Math.floor(toSec)}`, { signal });
  const json = await res.json();
  if (!json.success || !Array.isArray(json.data)) throw new Error(json.error || "No candles");
  return json.data as Candle[];
}

interface Window {
  symbol: string;
  tf: GameTf;
  candles: Candle[];
}

interface SignalWindow {
  symbol: string;
  candles: Candle[];
  list: ChartSignal[];
}

/** What a built question needs besides itself: the coin and moment it came from (revealed after answering). */
export interface Built {
  question: Question;
  coin: string | null;
  /** Unix second of "now" in the question's chart. */
  atSec: number | null;
}

/** The data for one session. Every window is fetched once and shared by all its games. */
export class SessionData {
  private windows = new Map<string, Promise<Window>>();
  private htf = new Map<string, Promise<Candle[]>>();
  /** One signal window per signals game: a random coin's month holds only a few signals, and two games on the same one would give each other away. */
  private signalWindows: Promise<SignalWindow | null>[] = [];
  private signalSlots = new Map<string, number>();
  private signalCoins = new Set<string>();
  private ctrl = new AbortController();

  constructor(private rng: Rng, private nowSec = Math.floor(Date.now() / 1000)) {}

  /** Stop every request still running (the session closed). */
  close() {
    this.ctrl.abort();
  }

  private window(tf: GameTf, slot: number): Promise<Window> {
    const key = `${tf}:${slot}`;
    let w = this.windows.get(key);
    if (!w) {
      const tfSec = TF_SEC[tf];
      const symbol = this.rng.pick(GAME_COINS);
      const end = randomWindowEnd(this.nowSec, tfSec, 2 * tfSec, this.rng);
      const from = end - WINDOW_BARS[tf] * tfSec;
      w = fetchCandles(symbol, tf, from, end, this.ctrl.signal).then(candles => ({ symbol, tf, candles }));
      // A failed window is fetched again next time (another coin).
      w.catch(() => this.windows.delete(key));
      this.windows.set(key, w);
    }
    return w;
  }

  private htfFor(w: Window): Promise<Candle[]> {
    const key = `${w.symbol}:${w.tf}`;
    let h = this.htf.get(key);
    if (!h) {
      const first = w.candles[0]!.time;
      const last = w.candles[w.candles.length - 1]!.time;
      h = fetchCandles(w.symbol, "4h", first - HTF_BEFORE * 14_400, last + TF_SEC[w.tf], this.ctrl.signal);
      h.catch(() => this.htf.delete(key));
      this.htf.set(key, h);
    }
    return h;
  }

  /**
   * The engine's signals for a random coin's random past month (null: none
   * to be had), one window per `slot`. Started early, they load while the
   * first games are played.
   */
  signals(slot = 0): Promise<SignalWindow | null> {
    while (this.signalWindows.length <= slot) this.signalWindows.push(this.loadSignals(3));
    return this.signalWindows[slot]!;
  }

  private async loadSignals(tries: number): Promise<SignalWindow | null> {
    for (let t = 0; t < tries; t++) {
      const fresh = GAME_COINS.filter(c => !this.signalCoins.has(c));
      const symbol = this.rng.pick(fresh.length > 0 ? fresh : GAME_COINS);
      this.signalCoins.add(symbol);
      const end = randomWindowEnd(this.nowSec, 3600, SIGNAL_AFTER_BARS * 3600 + DAY, this.rng);
      try {
        const timeout = AbortSignal.timeout(SIGNALS_TIMEOUT_MS);
        const signal = AbortSignal.any([timeout, this.ctrl.signal]);
        const [res, candles] = await Promise.all([
          fetch(`/api/trading-signals/chart/${symbol}?end=${end}&days=${SIGNAL_DAYS}`, { signal }).then(r => r.json() as Promise<ChartSignalsResponse>),
          fetchCandles(symbol, "1h", end - SIGNAL_DAYS * DAY - SIGNAL_HISTORY_BARS * 3600, end + SIGNAL_AFTER_BARS * 3600, signal),
        ]);
        if (!res.success) continue;
        const from = end - SIGNAL_DAYS * DAY;
        const list = res.signals.filter(s => s.signalTime > from && s.signalTime <= end);
        if (list.length > 0 && candles.length > SIGNAL_HISTORY_BARS) return { symbol, candles, list };
      } catch {
        if (this.ctrl.signal.aborted) return null;
      }
    }
    return null;
  }

  /** The data for one try of a game: a random moment in its timeframe's window. */
  async dataFor(game: GameDef, rng: Rng): Promise<GameData | null> {
    const needs = game.needs;
    if (!needs) return null;
    if (game.signals) {
      let slot = this.signalSlots.get(game.id);
      if (slot === undefined) {
        slot = this.signalSlots.size;
        this.signalSlots.set(game.id, slot);
      }
      const sw = await this.signals(slot);
      if (!sw) return null;
      return { symbol: sw.symbol, coin: coinOf(sw.symbol), tf: "1h", candles: sw.candles, ahead: [], signals: sw.list };
    }
    const w = await this.window(needs.tf, rng.int(0, WINDOWS_PER_TF - 1));
    const ahead = needs.ahead ?? 0;
    const lo = needs.history - 1;
    const hi = w.candles.length - 1 - ahead;
    if (hi < lo) return null;
    const i = rng.int(lo, hi);
    const data: GameData = {
      symbol: w.symbol,
      coin: coinOf(w.symbol),
      tf: needs.tf,
      candles: w.candles.slice(i - needs.history + 1, i + 1),
      ahead: w.candles.slice(i + 1, i + 1 + ahead),
    };
    if (needs.htf) {
      const nowSec = w.candles[i]!.time + TF_SEC[needs.tf];
      const h = await this.htfFor(w);
      data.htf = h.filter(c => c.time + 14_400 <= nowSec).slice(-HTF_BEFORE);
    }
    return data;
  }
}

/** Tries per question before the session swaps the game for another. */
const BUILD_TRIES = 12;

/**
 * One question for a game: random moments until the game makes a fair one
 * that passes the quality bar. Null when it can't (no data, or every try
 * was a close call).
 */
export async function buildQuestion(game: GameDef, data: SessionData, rng: Rng): Promise<Built | null> {
  for (let i = 0; i < BUILD_TRIES; i++) {
    const seed = rng.int(0, 2 ** 31 - 1);
    let d: GameData | null = null;
    if (game.needs) {
      try {
        d = await data.dataFor(game, makeRng(seed));
      } catch {
        d = null;
      }
      if (!d) {
        if (game.signals) return null; // its signal window is fixed for the session: more tries won't help
        continue;
      }
    }
    try {
      const q = game.make(d, makeRng(seed ^ 0x9e3779b9));
      if (!q) continue;
      const problems = questionProblems(q);
      if (problems.length > 0) {
        console.warn(`[games] ${game.id}: skipped a question`, problems);
        continue;
      }
      const last = q.chart?.candles[q.chart.candles.length - 1];
      return { question: q, coin: d?.coin ?? null, atSec: last && q.chart ? last.time + TF_SEC[q.chart.tf] : null };
    } catch (e) {
      console.warn(`[games] ${game.id}: make() failed`, e);
    }
  }
  return null;
}
